import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Inject,
  Input,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { interval, Subscription, timer } from 'rxjs';

export interface InstallStep {
  stepNumber: number;
  title: string;
  description: string;
}

@Component({
  selector: 'arkik-ios-pwa-prompt',
  templateUrl: './ios-pwa-animation.component.html',
  styleUrls: ['./ios-pwa-animation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IosPwaPromptComponent implements OnInit, OnDestroy {
  @Output() dismiss = new EventEmitter<void>();
  @Input() safari = true;

  currentStep = 1;
  isManualSelection = false;
  autoPlay = false;
  private stepSubscription?: Subscription;
  private resumeSubscription?: Subscription;

  readonly steps: InstallStep[] = [
    {
      stepNumber: 1,
      title: 'views.globals.pwa-install.step-share',
      description: 'views.globals.pwa-install.share',
    },
    {
      stepNumber: 2,
      title: 'views.globals.pwa-install.step-home-screen',
      description: 'views.globals.pwa-install.home-screen',
    },
    {
      stepNumber: 3,
      title: 'views.globals.pwa-install.step-confirm',
      description: 'views.globals.pwa-install.add',
    },
  ];

  constructor(
    private cdr: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  ngOnInit(): void {
    const browserWindow = this.document.defaultView;
    this.autoPlay = !!browserWindow && !browserWindow.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    this.startAutoCycle();
  }

  ngOnDestroy(): void {
    this.stopAutoCycle();
    this.resumeSubscription?.unsubscribe();
  }

  startAutoCycle(): void {
    this.stopAutoCycle();
    if (!this.autoPlay) {
      return;
    }
    this.stepSubscription = interval(3600).subscribe(() => {
      if (!this.isManualSelection) {
        this.currentStep = this.currentStep < this.steps.length ? this.currentStep + 1 : 1;
        this.cdr.markForCheck();
      }
    });
  }

  stopAutoCycle(): void {
    this.stepSubscription?.unsubscribe();
  }

  selectStep(step: number): void {
    if (!this.steps.some((installStep) => installStep.stepNumber === step)) {
      return;
    }
    this.stopAutoCycle();
    this.resumeSubscription?.unsubscribe();
    this.isManualSelection = true;
    this.currentStep = step;
    this.cdr.markForCheck();

    if (this.autoPlay) {
      this.resumeSubscription = timer(8000).subscribe(() => {
        this.isManualSelection = false;
        this.startAutoCycle();
        this.cdr.markForCheck();
      });
    }
  }

  toggleAutoCycle(): void {
    this.autoPlay = !this.autoPlay;
    this.isManualSelection = false;
    this.resumeSubscription?.unsubscribe();
    this.startAutoCycle();
  }

  closePrompt(): void {
    this.dismiss.emit();
  }
}
